import { css } from '@emotion/react';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { UIColorPalette } from './ColorPalette';

import { nonNull } from '@mythos/utils/utils';
import { graphql } from './generated/gql';
import { useGraphQLQuery } from './GraphQLHelpers';
import QueryStateRenderer from './QueryStateRenderer';

const GameHistoryListQuery = graphql(`
  query GameHistoryList($includeBots: Boolean) {
    developer {
      games(includeBots: $includeBots) {
        edges {
          node {
            id
            startTimestamp
            endTimestamp
            age
            players {
              user {
                id
                name
              }
            }
            ratingDeltas {
              userID
              score
              originalRating
              newRating
            }
          }
        }
      }
    }
  }
`);

type Props = {
  history: History;
};
export default function GameHistoryListPage(props: Props) {
  const [showBots, setShowBots] = React.useState(false);
  const navigate = useNavigate();

  const args = useMemo(
    () => ({
      includeBots: showBots,
    }),
    [showBots],
  );
  const state = useGraphQLQuery(GameHistoryListQuery, args);

  const onClickEntry = useCallback((gameID: string) => {
    navigate('/game/' + gameID);
  }, []);

  return (
    <div css={GameHistoryListStyles.page}>
      <h1 css={GameHistoryListStyles.title}>Game History</h1>
      <div css={GameHistoryListStyles.controls}>
        <label>
          <input
            type="checkbox"
            checked={showBots}
            onChange={(e) => setShowBots(e.target.checked)}
          />
          {' Show bot games'}
        </label>
      </div>
      <QueryStateRenderer state={state}>
        {(data) => {
          const gameList = data.developer?.games.edges
            .map((edge) => edge.node)
            .filter(nonNull);
          if (!gameList || gameList.length === 0) {
            return <p css={GameHistoryListStyles.emptyText}>No games found</p>;
          }

          const list = gameList.map((game) => {
            const date = new Date(game.startTimestamp);
            const playerMap = new Map(
              game.players.map((p) => [p.user?.id, p.user?.name]),
            );

            // Find winner from rating deltas (highest score)
            const ratingDeltas = game.ratingDeltas ?? [];
            const sortedByScore = [...ratingDeltas].sort(
              (a, b) => b.score - a.score,
            );
            const winner = sortedByScore[0];
            const winnerName = winner ? playerMap.get(winner.userID) : null;

            return (
              <tr key={game.id} css={GameHistoryListStyles.item}>
                <td css={GameHistoryListStyles.cell}>
                  {date.toLocaleDateString()} {date.toLocaleTimeString()}
                </td>
                <td css={GameHistoryListStyles.cell}>
                  {game.players.map((p) => p.user?.name).join(', ')}
                </td>
                <td css={GameHistoryListStyles.cell}>
                  {game.endTimestamp ? (
                    (winnerName ?? '—')
                  ) : (
                    <span css={GameHistoryListStyles.ongoingText}>Ongoing</span>
                  )}
                </td>
                <td css={GameHistoryListStyles.cell}>{game.age}</td>
                <td css={GameHistoryListStyles.cell}>
                  {game.endTimestamp
                    ? formatTime(game.endTimestamp - game.startTimestamp)
                    : '—'}
                </td>
                <td css={GameHistoryListStyles.cell}>
                  <button onClick={() => onClickEntry(game.id)}>View</button>
                </td>
              </tr>
            );
          });

          return (
            <div css={GameHistoryListStyles.container}>
              <table css={GameHistoryListStyles.table}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Players</th>
                    <th>Winner</th>
                    <th>Ages</th>
                    <th>Duration</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>{list}</tbody>
              </table>
            </div>
          );
        }}
      </QueryStateRenderer>
    </div>
  );
}

function formatTime(time: number): string {
  const minutes = Math.floor(time / 60000);
  const seconds = Math.floor((time % 60000) / 1000);
  return `${minutes}m${seconds}s`;
}

const GameHistoryListStyles = {
  page: css({
    padding: 16,
    maxWidth: 1000,
    margin: '0 auto',
  }),
  title: css({
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '1.5rem',
    textAlign: 'center',
  }),
  container: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  }),
  controls: css({
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  }),
  emptyText: css({
    color: 'rgb(150, 150, 150)',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 32,
  }),
  ongoingText: css({
    color: 'rgb(150, 150, 150)',
    fontStyle: 'italic',
  }),
  table: css({
    borderCollapse: 'collapse',
    backgroundColor: UIColorPalette.listBackground,
    '& th, & td': {
      padding: 10,
      textAlign: 'left',
      borderBottom: `1px solid ${UIColorPalette.listItemBottomBorder}`,
    },
    '& th': {
      fontWeight: 'bold',
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
    },
    '& tr:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
  }),
  item: css({}),
  cell: css({
    padding: '8px 12px',
  }),
};
